import { useQuery } from "@apollo/client";
import Button from "@/components/bricks/button/Button";
import { FormikProps } from "formik";
import { FULDMAEGTIGE_FOR_PERSON_BY_ID_QUERY } from "../../../api/person/queries";
import { getNumberOfFuldmaegtigeAtDate } from "../../../util/person";
import AutoCompleteFirma from "../../autocomplete/AutoCompleteFirma";
import { AutoCompleteFormSelectedItem } from "../../autocomplete/AutoCompleteListItem";
import AutoCompletePerson from "../../autocomplete/AutoCompletePerson";
import { FuldmaegtigeForPersonResponse } from "../../autocomplete/interface";
import { FormikDatePicker } from "../../formcontrols/DatePicker";
import { BooleanCheckboxFormControl } from "../../formcontrols/InputCheckbox";
import { TextInputFormik } from "../../formcontrols/InputItem";
import InputRadio from "../../formcontrols/InputRadio";
import { FormikTextArea } from "../../formcontrols/TextArea";
import ValidationErrorMessage from "../../formcontrols/ValidationErrorMessage";
import UdenlandskIcon from "../../icons/UdenlandskIcon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../select/Select";
import { StickyFooter } from "../../stickyFooter/modalStickyFooter";
import {
  CreatePersonKodeDict,
  FirmaRelationKodeDict,
  OpretPersonStep1Formik,
  OpretPersonStep2Formik,
} from "./useOpretPerson";
import { useUdenlandskeAdvokatsamfund } from "../../../hooks/useQueries";
import AddressFormControl from "../../formcontrols/AddressFormControl";
import SuperDropdown from "../../dropdown/SuperDropdown";
import DefaultDropdownItem from "../../dropdown/DefaultDropdownItem";
import { AlertMessage } from "../../AlertMessage";
import IsRequired from "../../formcontrols/common/IsRequired";
import { AddressGenericFormSet } from "../../formcontrols/sets/AddressGenericFormSet";
import { useState } from "react";
import { orderBy } from "../../../util/orderBy";

export default function Step2({
  setStep,
  step1,
  step2,
}: {
  setStep: React.Dispatch<React.SetStateAction<number>>;
  step1: FormikProps<OpretPersonStep1Formik>;
  step2: FormikProps<OpretPersonStep2Formik>;
}) {
  const {
    getUdenlandskAdvokatsamfundError,
    getUdenlandskAdvokatsamfundIsLoading,
    udenlandskeAdvokatsamfund,
  } = useUdenlandskeAdvokatsamfund();
  const {
    harFirmaRelation,
    harDanskAdresse,
    danskAdresse,
    // firma,
    // udenlandskAdresse,
  } = step2.values;
  const { advokat, advokatfuldmaegtig, euadvokat, ovrig } =
    CreatePersonKodeDict;
  const {
    data: personFuldmaegtige,
    // loading
  } = useQuery<FuldmaegtigeForPersonResponse>(
    FULDMAEGTIGE_FOR_PERSON_BY_ID_QUERY,
    {
      variables: {
        id: step2.values.principal?.id ?? "",
      },
      skip: !step2.values.principal,
    },
  );

  const numberOfFuldmaegtigeAtStartDate = getNumberOfFuldmaegtigeAtDate(
    personFuldmaegtige,
    step2.values.startDato,
  );

  const [focusPersonAutocompleteOnChange, setFocusPersonAutocompleteOnChange] =
    useState(false);
  const [focusFirmaComponentOnChange, setFocusFirmaComponentOnChange] =
    useState(false);
  const [
    focusUdenlandskAdvokatsamfundComponentOnChange,
    setFocusUdenlandskAdvokatsamfundComponentOnChange,
  ] = useState(false);
  return (
    <form autoComplete="off" onSubmit={step2.handleSubmit}>
      <div className="c-item-list c-modal-container-inner">
        {step1.values.createPerson.kode === euadvokat.kode && (
          <>
            <h3 className="mb-n2 mt-2">EU-advokat</h3>
            {/* Vises kun hvis EU-advokat er valgt */}
            <div className="flex-100 align-items-stretch">
              <label
                className="form-label"
                htmlFor="wizard-udenlandsk-advokatsamfund"
              >
                Udenlandsk advokatsamfund*
              </label>
              {step2.values.udenlandskAdvokatSamfund && (
                <AutoCompleteFormSelectedItem
                  title={"Fjern udenlandsk advokatsamfund"}
                  item={{
                    header: step2.values.udenlandskAdvokatSamfund.navn,
                    subheader:
                      step2.values.udenlandskAdvokatSamfund.beskrivelse,
                    icon: <UdenlandskIcon height={22} />,
                    id: "ua",
                  }}
                  onClick={() => {
                    setFocusUdenlandskAdvokatsamfundComponentOnChange(true);
                    step2.setTouched({
                      ...step2.touched,
                      udenlandskAdvokatSamfund: true,
                    });
                    step2.setFieldValue("udenlandskAdvokatSamfund", undefined);
                  }}
                  focusOnMount={focusUdenlandskAdvokatsamfundComponentOnChange}
                />
              )}
              {!step2.values.udenlandskAdvokatSamfund &&
                !udenlandskeAdvokatsamfund &&
                getUdenlandskAdvokatsamfundIsLoading && (
                  <div>Henter udenlandske advokatsamfund</div>
                )}
              {!step2.values.udenlandskAdvokatSamfund &&
                !udenlandskeAdvokatsamfund &&
                !!getUdenlandskAdvokatsamfundError && (
                  <div>Kunne ikke hente udenlandske advokatsamfund</div>
                )}
              {!step2.values.udenlandskAdvokatSamfund &&
                udenlandskeAdvokatsamfund && (
                  <SuperDropdown
                    classNameContainer={
                      step2.touched.udenlandskAdvokatSamfund &&
                      step2.errors.udenlandskAdvokatSamfund
                        ? "is-invalid"
                        : ""
                    }
                    items={orderBy(
                      udenlandskeAdvokatsamfund,
                      "UdenlandskadvokatsamfundNr",
                    )}
                    itemsIdKey="id"
                    itemToString={(item) => item?.navn ?? ""}
                    itemToElement={(item) => (
                      <DefaultDropdownItem label={item.navn} />
                    )}
                    filterKeys={["navn", "beskrivelse"]}
                    onSelectedItemEvent={(item) => {
                      if (item.selectedItem) {
                        setFocusUdenlandskAdvokatsamfundComponentOnChange(true);
                        step2.setTouched({
                          ...step2.touched,
                          udenlandskAdvokatSamfund: true,
                        });
                        step2.setFieldValue(
                          "udenlandskAdvokatSamfund",
                          item.selectedItem,
                        );
                      }
                    }}
                    focusInputOnMount={
                      focusUdenlandskAdvokatsamfundComponentOnChange
                    }
                  />
                )}
              <ValidationErrorMessage
                touched={step2.touched.udenlandskAdvokatSamfund}
                errorMessage={step2.errors.udenlandskAdvokatSamfund}
              />
            </div>
          </>
        )}
        {(step1.values.createPerson.kode === advokat.kode ||
          step1.values.createPerson.kode === euadvokat.kode) && (
          <>
            <h3 className="mb-n2 mt-2">Beskikkelse</h3>
            {/* Vises kun hvis EU-advokat eller Advokat er valgt */}
            <div className="flex-100">
              <FormikDatePicker
                config={{
                  component: "datepicker",
                  id: "startDato",
                  label: "Startdato",
                  isRequired: true,
                  initialValue: "",
                }}
                formik={step2}
              />
            </div>
            <div className="flex-100">
              <FormikTextArea
                config={{
                  component: "textarea",
                  id: "beskikkelseAarsag",
                  label: "Årsag",
                  placeholder: "Angiv årsag",
                  initialValue: "",
                }}
                formik={step2}
              />
            </div>
            {step1.values.createPerson.kode === advokat.kode && (
              <div className="form-check d-block">
                {/* <input
                  className="form-check-input"
                  type="checkbox"
                  id="straksDeponering"
                  onChange={step2.handleChange}
                  onBlur={step2.handleBlur}
                  checked={step2.values.straksDeponering}
                />
                <label className="form-check-label" htmlFor="straksDeponering">
                  Samtidig deponering
                </label> */}
                <BooleanCheckboxFormControl
                  config={{
                    component: "checkbox",
                    label: "",
                    id: "straksDeponering",
                    inputLabel: "Samtidig deponering",
                  }}
                  formik={step2}
                />
              </div>
            )}
          </>
        )}
        {step1.values.createPerson.kode === advokatfuldmaegtig.kode && (
          <>
            <h3 className="mb-n2 mt-2 flex-100">Principal</h3>
            <div className="flex-100 align-items-stretch">
              <label className="form-label">
                Principalnavn <IsRequired />
              </label>
              {!step2.values.principal && (
                <AutoCompletePerson
                  isInvalidClass={
                    step2.touched.principal && step2.errors.principal
                      ? "is-invalid"
                      : ""
                  }
                  placeholder="Søg og vælg principal ..."
                  onSelectedItemChange={(item) => {
                    setFocusPersonAutocompleteOnChange(true);
                    step2.setTouched({ ...step2.touched, principal: true });
                    step2.setFieldValue("principal", item.selectedItem);
                  }}
                  includeFirstItem={false}
                  erUdoevendeAdvokat={true}
                  focusInputOnMount={focusPersonAutocompleteOnChange}
                />
              )}
              {step2.values.principal && (
                <AutoCompleteFormSelectedItem
                  title={"Fjern principal"}
                  item={step2.values.principal}
                  onClick={() => {
                    setFocusPersonAutocompleteOnChange(true);
                    step2.setFieldValue("principal", undefined);
                  }}
                  focusOnMount={focusPersonAutocompleteOnChange}
                />
              )}
              <ValidationErrorMessage
                touched={step2.touched.principal}
                errorMessage={step2.errors.principal}
              />
            </div>
            <div className="flex-100">
              <FormikDatePicker
                config={{
                  component: "datepicker",
                  id: "startDato",
                  label: "Startdato",
                  isRequired: true,
                  initialValue: "",
                }}
                formik={step2}
              />
            </div>
            {numberOfFuldmaegtigeAtStartDate > 1 && (
              <AlertMessage notificationIcon>
                <>
                  <b className="fw-semibold">Bemærk:</b> Den valgte advokat er
                  allerede principal for{" "}
                  <b>{numberOfFuldmaegtigeAtStartDate}</b> fuldmægtige på
                  starttidspunktet.
                </>
              </AlertMessage>
            )}
          </>
        )}

        {!step2.values.straksDeponering && (
          <>
            <h3 className="mb-n2 mt-2 flex-100">Firmarelation</h3>
            {step1.values.createPerson.kode === ovrig.kode && (
              <div className="flex-100 d-block">
                <InputRadio
                  className="form-check-inline"
                  label="Har firmarelation"
                  name={"harFirmaRelation"}
                  id="option1"
                  checked={harFirmaRelation ? true : false}
                  onChange={() => {
                    step2.setFieldValue("harFirmaRelation", true, true);
                  }}
                />
                <InputRadio
                  className="form-check-inline"
                  label="Har ikke firmarelation"
                  name={"harFirmaRelation"}
                  id="option2"
                  checked={harFirmaRelation ? false : true}
                  onChange={() => {
                    step2.setFieldValue("harFirmaRelation", false, true);
                  }}
                />
              </div>
            )}

            {(step1.values.createPerson.kode !==
              CreatePersonKodeDict.ovrig.kode ||
              harFirmaRelation) && (
              <>
                <div className="flex-100 align-items-stretch">
                  <label className="form-label" htmlFor="wizard-firmanavn">
                    Firma*
                  </label>
                  {!step2.values.firma && (
                    <AutoCompleteFirma
                      isInvalidClass={
                        step2.touched.firma && step2.errors.firma
                          ? "is-invalid"
                          : ""
                      }
                      onInputBlur={() => {
                        step2.setFieldTouched("firma", true);
                      }}
                      includeFirstItem={false}
                      // key={step2.firma?.id}
                      onSelectedItemChange={(item) => {
                        setFocusFirmaComponentOnChange(true);
                        step2.setTouched({ ...step2.touched, firma: true });
                        step2.setFieldValue("firma", item.selectedItem);
                      }}
                      focusInputOnMount={focusFirmaComponentOnChange}
                    />
                  )}
                  {step2.values.firma && (
                    <AutoCompleteFormSelectedItem
                      title={"Fjern firma"}
                      item={step2.values.firma}
                      onClick={() => {
                        setFocusFirmaComponentOnChange(true);
                        step2.setFieldValue("firma", undefined);
                      }}
                      focusOnMount={focusFirmaComponentOnChange}
                    />
                  )}
                  <ValidationErrorMessage
                    touched={step2.touched.firma}
                    errorMessage={step2.errors.firma}
                  />
                </div>
                <div className="flex-100">
                  <TextInputFormik
                    config={{
                      component: "textinput",
                      id: "emailArbejde",
                      label: "E-mail, arbejde",
                      placeholder: "Indtast e-mail",
                      initialValue: "",
                    }}
                    formik={step2}
                  />
                </div>
                <div>
                  <FormikDatePicker
                    formik={step2}
                    config={{
                      component: "datepicker",
                      label: "Startdato",
                      isRequired: true,
                      id: "firmaStartDato",
                      initialValue: "",
                    }}
                  />
                </div>
                <div>
                  <Select
                    value={step2.values.firmaRelation.kode.toString()}
                    onValueChange={(value) => {
                      switch (value) {
                        case FirmaRelationKodeDict.ansat.kode.toString(): {
                          step2.setFieldValue(
                            "firmaRelation",
                            FirmaRelationKodeDict.ansat,
                            true,
                          );
                          break;
                        }
                        case FirmaRelationKodeDict.partner.kode.toString(): {
                          step2.setFieldValue(
                            "firmaRelation",
                            FirmaRelationKodeDict.partner,
                            true,
                          );
                          break;
                        }
                        case FirmaRelationKodeDict.ejer.kode.toString(): {
                          step2.setFieldValue(
                            "firmaRelation",
                            FirmaRelationKodeDict.ejer,
                            true,
                          );
                          break;
                        }
                      }
                    }}
                  >
                    <label className="form-label">{"Firmarelation"}</label>
                    <SelectTrigger />
                    <SelectContent>
                      <SelectItem
                        value={FirmaRelationKodeDict.ansat.kode.toString()}
                      >
                        {FirmaRelationKodeDict.ansat.beskrivelse}
                      </SelectItem>
                      <SelectItem
                        value={FirmaRelationKodeDict.partner.kode.toString()}
                      >
                        {FirmaRelationKodeDict.partner.beskrivelse}
                      </SelectItem>
                      <SelectItem
                        value={FirmaRelationKodeDict.ejer.kode.toString()}
                      >
                        {FirmaRelationKodeDict.ejer.beskrivelse}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </>
        )}
        {step1.values.createPerson.kode === ovrig.kode && !harFirmaRelation && (
          <>
            <h3 className="mb-n2 mt-2 flex-100">Kontaktoplysninger</h3>
            <div className="flex-100 d-block">
              <InputRadio
                className="form-check-inline"
                label="Dansk adresse"
                name={"harDanskAdresse"}
                id="harDanskAdresse1"
                checked={harDanskAdresse ? true : false}
                onChange={() => {
                  step2.setFieldValue("harDanskAdresse", true, true);
                }}
              />
              <InputRadio
                className="form-check-inline"
                label="Udenlandsk adresse"
                name={"harDanskAdresse"}
                id="harDanskAdresse2"
                checked={harDanskAdresse ? false : true}
                onChange={() => {
                  step2.setFieldValue("harDanskAdresse", false, true);
                  step2.setFieldValue("danskAdresse", undefined, true);
                }}
              />
            </div>
            {harDanskAdresse ? (
              <div className="flex-100">
                <AddressFormControl
                  config={{
                    component: "adresse",
                    id: "danskAdresse",
                    label: "Adresse",
                    isRequired: true,
                  }}
                  formik={step2}
                  value={danskAdresse}
                />
              </div>
            ) : (
              <AddressGenericFormSet formik={step2} title={null} />
            )}

            <div className="flex-100">
              <TextInputFormik
                config={{
                  component: "textinput",
                  id: "bemaerkning",
                  initialValue: "",
                  label: "Bemærkning",
                  isRequired: false,
                }}
                formik={step2}
              />
            </div>
            <div>
              <FormikDatePicker
                formik={step2}
                config={{
                  component: "datepicker",
                  label: "Startdato",
                  isRequired: true,
                  id: "periodeFraDato",
                  initialValue: step2.values.periodeFraDato,
                }}
              />
            </div>
            <div>
              <FormikDatePicker
                formik={step2}
                config={{
                  component: "datepicker",
                  label: "Slutdato",
                  id: "periodeTilDatoInklusiv",
                  initialValue: step2.values.periodeTilDatoInklusiv,
                }}
              />
            </div>
            <div className="flex-100">
              <TextInputFormik
                config={{
                  component: "textinput",
                  id: "emailPrivat",
                  label: "E-mail, privat",
                  placeholder: "Indtast e-mail",
                  initialValue: "",
                }}
                formik={step2}
              />
            </div>
          </>
        )}
      </div>
      <StickyFooter>
        <Button
          color="secondary"
          onClick={() => {
            setStep(1);
          }}
        >
          Forrige
        </Button>
        <Button type="submit" color="primary">
          Opret
        </Button>
      </StickyFooter>
    </form>
  );
}
